import {
    $applyNodeReplacement,
    LexicalNode,
    NodeKey,
    ParagraphNode,
    SerializedParagraphNode,
    Spread,
    DOMExportOutput,
    EditorConfig,
    RangeSelection,
    ElementFormatType,
    LexicalEditor
} from "lexical"

export type SerializedDirectionalParagraphNode = Spread<
    {
        type: "directional-paragraph"
        version: 1
        direction: "ltr" | "rtl"
    },
    SerializedParagraphNode
>

export class DirectionalParagraphNode extends ParagraphNode {
    __direction: "ltr" | "rtl"

    constructor(direction: "ltr" | "rtl", key?: NodeKey) {
        super(key)
        this.__direction = direction
    }

    static getType(): string {
        return "directional-paragraph"
    }

    static clone(node: DirectionalParagraphNode): DirectionalParagraphNode {
        return new DirectionalParagraphNode(node.__direction, node.__key)
    }

    createDOM(config: EditorConfig): HTMLElement {
        const element = super.createDOM(config)
        element.dir = this.__direction
        element.style.direction = this.__direction
        element.style.unicodeBidi = "embed"
        element.setAttribute("data-lexical-direction", this.__direction)

        // Always respect the format type for text alignment
        const format = this.getFormatType()

        // Apply text alignment based on format
        if (format === "left" || format === "right" || format === "center" || format === "justify") {
            element.style.textAlign = format
        } else {
            // Only use direction-based alignment as fallback
            element.style.textAlign = this.__direction === "rtl" ? "right" : "left"
        }

        return element
    }

    updateDOM(prevNode: DirectionalParagraphNode): boolean {
        const hasDOMDirectionChanged = prevNode.__direction !== this.__direction
        return hasDOMDirectionChanged
    }

    setDirection(direction: "ltr" | "rtl"): this {
        const writable = this.getWritable()
        writable.__direction = direction
        return this
    }

    getDirection(): "ltr" | "rtl" {
        return this.__direction
    }

    static importJSON(serializedNode: SerializedDirectionalParagraphNode): DirectionalParagraphNode {
        const node = $createDirectionalParagraphNode(serializedNode.direction)
        node.setFormat(serializedNode.format)
        node.setIndent(serializedNode.indent)
        node.setDirection(serializedNode.direction)
        return node
    }

    exportJSON(): SerializedDirectionalParagraphNode {
        return {
            ...super.exportJSON(),
            direction: this.__direction,
            type: "directional-paragraph",
            version: 1
        }
    }

    exportDOM(editor: LexicalEditor): DOMExportOutput {
        // Call the parent to get a <p> element and default children
        const { element } = super.exportDOM(editor)

        if (element instanceof HTMLElement) {
            // Apply directional attributes
            element.dir = this.__direction
            element.style.direction = this.__direction
            element.style.unicodeBidi = "embed"
            element.setAttribute("data-lexical-direction", this.__direction)

            // Always respect the format type for text alignment
            const format = this.getFormatType()

            // Apply text alignment based on format
            if (format === "left" || format === "right" || format === "center" || format === "justify") {
                element.style.textAlign = format
            } else {
                // Only use direction-based alignment as fallback
                element.style.textAlign = this.__direction === "rtl" ? "right" : "left"
            }
        }

        return { element }
    }

    insertNewAfter(_: RangeSelection, restoreSelection = true): ParagraphNode {
        const newElement = new DirectionalParagraphNode(this.__direction)
        newElement.setFormat(this.getFormat() as unknown as ElementFormatType)
        newElement.setIndent(this.getIndent())
        this.insertAfter(newElement, restoreSelection)
        return newElement
    }
}

export function $createDirectionalParagraphNode(direction: "ltr" | "rtl"): DirectionalParagraphNode {
    return $applyNodeReplacement(new DirectionalParagraphNode(direction))
}

export function $isDirectionalParagraphNode(node: LexicalNode | null | undefined): node is DirectionalParagraphNode {
    return node instanceof DirectionalParagraphNode
}
